import React, { useCallback, useRef } from "react";
import { classnames, Button, Icon } from "@culturehq/components";

import { useDocumentEvent, useDoor } from "app/hooks";
import { containedBy, t, Trans } from "app/utils";

import facebook from "images/social/facebook.svg";
import linkedin from "images/social/linkedin.svg";
import twitter from "images/social/twitter.svg";
import instagram from "images/social/instagram.svg";
import tiktok from "images/social/tiktok.svg";
import snapchat from "images/social/snapchat.svg";
import slack from "images/social/slack.png";

import styles from "./styles.module.scss";

const UserSocialLinks = ({ profile }) => {
  const { social, slackId, slackTeamId } = profile;

  const getSocialIcon = ({ socialMediaType: { name } }) => {
    switch (name) {
      case "Instagram":
        return instagram;
      case "Facebook":
        return facebook;
      case "Linkedin":
        return linkedin;
      case "Twitter":
        return twitter;
      case "TikTok":
        return tiktok;
      case "Snapchat":
        return snapchat;
      default:
        return "";
    }
  };

  const getLinkText = ({ socialMediaType, username }) => {
    switch (socialMediaType.name) {
      case "Facebook":
      case "Linkedin":
        return "View profile";
      default:
        return username;
    }
  };

  return (
    <ul className="dropdown-menu social-dropdown-menu">
      {
        slackId && (
          <li>
            <a className="social-link" href={`slack://user?team=${slackTeamId}&id=${slackId}`} target="_blank" rel="noopener noreferrer">
              <img className="social-icon" src={slack} alt="Slack icon" />{" "}
              <Trans>Send message</Trans>
            </a>
          </li>
        )
      }
      {social.map(media => (
        <li key={media.id}>
          <a className="social-link" href={media.profileUrl} target="_blank" rel="noopener noreferrer">
            <img className="social-icon" src={getSocialIcon(media)} alt={media.socialMediaType.name} />{" "}
            <Trans>{getLinkText(media)}</Trans>
          </a>
        </li>
      ))}
    </ul>
  );
};

const SocialLinksButton = ({ children, profile }) => {
  const { open, onClose, onToggle } = useDoor(false);
  const { social, slackId, slackTeamId } = profile;
  const containerRef = useRef();

  useDocumentEvent("click", useCallback(
    event => {
      if (!containedBy(event.target, "chq-mdl") && containerRef.current && !containerRef.current.contains(event.target)) {
        onClose();
      }
    },
    [onClose]
  ));

  const classList = classnames(
    "QuickOptions",
    styles.quick,
    open && styles["quick-open"]
  );

  const getSocialMediaIcon = () => social[0].socialMediaType.name.toLowerCase();
  const getSocialMediaLink = () => social[0].profileUrl;

  const showIcon = () => {
    if (slackId) {
      if (social.length > 0) {
        return (<Icon icon="slack" />);
      }

      return (
        <a className="social-link" href={`slack://user?team=${slackTeamId}&id=${slackId}`} target="_blank" rel="noopener noreferrer">
          <Icon icon="slack" />
        </a>
      );
    }

    if (social.length > 1) {
      return (<Icon icon={getSocialMediaIcon()} />);
    }

    return (
      <a className="social-link" href={getSocialMediaLink()} target="_blank" rel="noopener noreferrer">
        <Icon icon={getSocialMediaIcon()} />
      </a>
    );
  };

  return (
    <div ref={containerRef} className={classList}>
      <Button aria-label={t("Open social menu")} className="social-button" inverted onClick={onToggle}>
        {showIcon()}
      </Button>
      {open && (
        <div className={classnames("QuickOptions__menu-wrapper", styles["quick--wrap"])}>
          <div className={classnames("QuickOptions__menu box-shadow", styles["quick--menu"])}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

const SocialLinksBox = ({ children, profile }) => (
  <SocialLinksButton profile={profile}>{children}</SocialLinksButton>
);

const SocialLinks = ({ user }) => {
  const { social, slackId } = user;

  const getMenu = () => {
    if (slackId) {
      if (social.length > 0) {
        return (<UserSocialLinks profile={user} />);
      }

      return (<></>);
    }

    if (social.length > 1) {
      return (<UserSocialLinks profile={user} />);
    }

    return (<></>);
  };

  return (
    <SocialLinksBox profile={user}>
      {getMenu()}
    </SocialLinksBox>
  );
};

export default SocialLinks;
